import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { AuthService } from '../auth/auth.service';
import { PendingRegistrationService } from '../auth/pending-registration.service';
import { FirebaseService } from '../firebase/firebase.service';

// True while the signed-in session still has registration work to finish on /verify-email: the
// email is unverified, or the verified user never consumed the stashed register-form data (they
// clicked the link in another tab and came back later — the profile doc still has to be created).
const needsVerificationStep = (auth: AuthService, pending: PendingRegistrationService): boolean => {
  const user = auth.user();
  if (!user) {
    return false;
  }
  return !user.emailVerified || pending.has(user.uid);
};

export const authGuard: CanActivateFn = async () => {
  const auth = inject(AuthService);
  const pending = inject(PendingRegistrationService);
  const firebase = inject(FirebaseService);
  const router = inject(Router);
  if (!firebase.configured()) {
    return router.createUrlTree(['/setup']);
  }
  await auth.whenReady();
  if (!auth.user()) {
    return router.createUrlTree(['/login']);
  }
  return needsVerificationStep(auth, pending) ? router.createUrlTree(['/verify-email']) : true;
};

export const anonymousGuard: CanActivateFn = async () => {
  const auth = inject(AuthService);
  const pending = inject(PendingRegistrationService);
  const firebase = inject(FirebaseService);
  const router = inject(Router);
  if (!firebase.configured()) {
    return router.createUrlTree(['/setup']);
  }
  await auth.whenReady();
  if (!auth.user()) {
    return true;
  }
  return router.createUrlTree([needsVerificationStep(auth, pending) ? '/verify-email' : '/app/home']);
};

// The verify-email page needs a signed-in session (verified or not — the page itself finishes a
// pending registration and forwards already-verified visitors).
export const verifyEmailGuard: CanActivateFn = async () => {
  const auth = inject(AuthService);
  const firebase = inject(FirebaseService);
  const router = inject(Router);
  if (!firebase.configured()) {
    return router.createUrlTree(['/setup']);
  }
  await auth.whenReady();
  return auth.user() ? true : router.createUrlTree(['/login']);
};
